// Sandboxed SDUI expression evaluator (compath SDUI contract v1).
// NOT JavaScript: a tiny AST interpreter over the grammar in
// sdui/schema/expression-grammar.md. No eval/Function, no host access, no globals.

export type Scope = Record<string, unknown>;

// ---------- lexer ----------
type Tok =
  | { t: "num"; v: number }
  | { t: "str"; v: string }
  | { t: "ident"; v: string }
  | { t: "op"; v: string }
  | { t: "eof" };

const OPS3: string[] = [];
const OPS2 = ["||", "&&", "==", "!=", "<=", ">="];
const OPS1 = ["<", ">", "+", "-", "*", "/", "%", "!", "(", ")", ",", ".", "[", "]", "?", ":"];

function lex(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  const n = src.length;
  const isIdStart = (c: string) => /[A-Za-z_]/.test(c);
  const isId = (c: string) => /[A-Za-z0-9_]/.test(c);
  const isDigit = (c: string) => c >= "0" && c <= "9";
  while (i < n) {
    const c = src[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") { i++; continue; }
    if (c === "'") {
      let j = i + 1;
      let s = "";
      while (j < n && src[j] !== "'") { s += src[j]; j++; }
      if (j >= n) throw new Error("unterminated string");
      toks.push({ t: "str", v: s });
      i = j + 1;
      continue;
    }
    if (isDigit(c) || (c === "." && isDigit(src[i + 1] ?? ""))) {
      let j = i;
      while (j < n && (isDigit(src[j]) || src[j] === ".")) j++;
      toks.push({ t: "num", v: Number(src.slice(i, j)) });
      i = j;
      continue;
    }
    if (isIdStart(c)) {
      let j = i;
      while (j < n && isId(src[j])) j++;
      toks.push({ t: "ident", v: src.slice(i, j) });
      i = j;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (OPS2.includes(two)) { toks.push({ t: "op", v: two }); i += 2; continue; }
    if (OPS1.includes(c)) { toks.push({ t: "op", v: c }); i += 1; continue; }
    throw new Error(`unexpected char '${c}' in expression`);
  }
  void OPS3;
  toks.push({ t: "eof" });
  return toks;
}

// ---------- AST ----------
type Node =
  | { k: "lit"; v: unknown }
  | { k: "path"; segs: (string | number)[] }
  | { k: "call"; name: string; args: Node[] }
  | { k: "un"; op: string; e: Node }
  | { k: "bin"; op: string; l: Node; r: Node }
  | { k: "tern"; c: Node; a: Node; b: Node };

const BUILTINS = new Set(["len", "empty", "coalesce", "upper", "lower", "format", "contains", "not"]);

class Parser {
  private p = 0;
  constructor(private toks: Tok[]) {}
  private peek(): Tok { return this.toks[this.p]; }
  private next(): Tok { return this.toks[this.p++]; }
  private eat(v: string) {
    const t = this.next();
    if (t.t !== "op" || t.v !== v) throw new Error(`expected '${v}'`);
  }
  private isOp(v: string): boolean {
    const t = this.peek();
    return t.t === "op" && t.v === v;
  }

  parse(): Node {
    const e = this.ternary();
    if (this.peek().t !== "eof") throw new Error("trailing tokens in expression");
    return e;
  }
  private ternary(): Node {
    const c = this.or();
    if (this.isOp("?")) {
      this.next();
      const a = this.ternary();
      this.eat(":");
      const b = this.ternary();
      return { k: "tern", c, a, b };
    }
    return c;
  }
  private binL(next: () => Node, ops: string[]): Node {
    let l = next();
    while (this.peek().t === "op" && ops.includes((this.peek() as any).v)) {
      const op = (this.next() as any).v as string;
      const r = next();
      l = { k: "bin", op, l, r };
    }
    return l;
  }
  private or(): Node { return this.binL(() => this.and(), ["||"]); }
  private and(): Node { return this.binL(() => this.equality(), ["&&"]); }
  private equality(): Node { return this.binL(() => this.comparison(), ["==", "!="]); }
  private comparison(): Node { return this.binL(() => this.additive(), ["<", "<=", ">", ">="]); }
  private additive(): Node { return this.binL(() => this.multiplicative(), ["+", "-"]); }
  private multiplicative(): Node { return this.binL(() => this.unary(), ["*", "/", "%"]); }
  private unary(): Node {
    if (this.isOp("!") || this.isOp("-")) {
      const op = (this.next() as any).v as string;
      return { k: "un", op, e: this.unary() };
    }
    return this.primary();
  }
  private primary(): Node {
    const t = this.peek();
    if (t.t === "num") { this.next(); return { k: "lit", v: t.v }; }
    if (t.t === "str") { this.next(); return { k: "lit", v: t.v }; }
    if (this.isOp("(")) {
      this.next();
      const e = this.ternary();
      this.eat(")");
      return e;
    }
    if (t.t === "ident") {
      if (t.v === "true") { this.next(); return { k: "lit", v: true }; }
      if (t.v === "false") { this.next(); return { k: "lit", v: false }; }
      if (t.v === "null") { this.next(); return { k: "lit", v: null }; }
      // call?
      if (this.toks[this.p + 1]?.t === "op" && (this.toks[this.p + 1] as any).v === "(") {
        const name = t.v;
        if (!BUILTINS.has(name)) throw new Error(`unknown function '${name}'`);
        this.next(); // ident
        this.eat("(");
        const args: Node[] = [];
        if (!this.isOp(")")) {
          args.push(this.ternary());
          while (this.isOp(",")) { this.next(); args.push(this.ternary()); }
        }
        this.eat(")");
        return { k: "call", name, args };
      }
      return this.path();
    }
    throw new Error("unexpected token in expression");
  }
  private path(): Node {
    const first = this.next();
    if (first.t !== "ident") throw new Error("expected identifier");
    const segs: (string | number)[] = [first.v];
    for (;;) {
      if (this.isOp(".")) {
        this.next();
        const id = this.next();
        if (id.t !== "ident") throw new Error("expected property name");
        segs.push(id.v);
      } else if (this.isOp("[")) {
        this.next();
        const idx = this.next();
        if (idx.t !== "num") throw new Error("array index must be a number literal");
        this.eat("]");
        segs.push(idx.v);
      } else break;
    }
    return { k: "path", segs };
  }
}

// ---------- evaluator ----------
function truthy(v: unknown): boolean {
  if (v === null || v === undefined || v === false) return false;
  if (v === 0 || v === "") return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return true;
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v as object).length === 0;
  return false;
}

function fmtNumber(n: unknown, pattern: string): string {
  if (n === null || n === undefined || n === "") return "";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  const decimals = pattern.includes(".") ? (pattern.split(".")[1] || "").length : 0;
  if (pattern.includes(",")) {
    return num.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return decimals ? num.toFixed(decimals) : String(num);
}

function callBuiltin(name: string, args: unknown[]): unknown {
  switch (name) {
    case "len": {
      const x = args[0];
      if (x === null || x === undefined) return 0;
      if (typeof x === "string" || Array.isArray(x)) return x.length;
      if (typeof x === "object") return Object.keys(x as object).length;
      return 0;
    }
    case "empty": return isEmpty(args[0]);
    case "not": return !truthy(args[0]);
    case "coalesce": {
      for (const a of args) if (a !== null && a !== undefined) return a;
      return null;
    }
    case "upper": return args[0] == null ? null : String(args[0]).toUpperCase();
    case "lower": return args[0] == null ? null : String(args[0]).toLowerCase();
    case "format": return fmtNumber(args[0], String(args[1] ?? ""));
    case "contains": {
      const hay = args[0];
      const needle = args[1];
      if (typeof hay === "string") return hay.includes(String(needle));
      if (Array.isArray(hay)) return hay.some((x) => deepEq(x, needle));
      return false;
    }
    default: throw new Error(`unknown function '${name}'`);
  }
}

function deepEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  const ta = a === null ? "null" : Array.isArray(a) ? "arr" : typeof a;
  const tb = b === null ? "null" : Array.isArray(b) ? "arr" : typeof b;
  if (ta !== tb) return false;
  if (ta === "arr" || ta === "object") return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

function num(x: unknown): number | null {
  if (x === null || x === undefined) return null;
  const n = Number(x);
  return Number.isNaN(n) ? null : n;
}

const BLOCKED_KEYS = new Set(["__proto__", "constructor", "prototype"]);

// Read a single path segment safely: own properties only, no prototype-chain escape.
function safeGet(obj: unknown, key: string | number): unknown {
  if (obj === null || obj === undefined) return null;
  if (typeof key === "string" && BLOCKED_KEYS.has(key)) return null;
  if (Array.isArray(obj)) {
    if (typeof key === "number") return obj[key] ?? null;
    if (key === "length") return obj.length;
    return null;
  }
  if (typeof obj === "string") {
    if (key === "length") return obj.length;
    if (typeof key === "number") return obj[key] ?? null;
    return null;
  }
  if (typeof obj === "object") {
    return Object.prototype.hasOwnProperty.call(obj, key) ? (obj as any)[key] : null;
  }
  return null;
}

function evalNode(node: Node, scope: Scope): unknown {
  switch (node.k) {
    case "lit": return node.v;
    case "path": {
      let cur: unknown = safeGet(scope, node.segs[0]);
      for (let i = 1; i < node.segs.length; i++) {
        if (cur === null || cur === undefined) return null;
        cur = safeGet(cur, node.segs[i]);
      }
      return cur === undefined ? null : cur;
    }
    case "call": return callBuiltin(node.name, node.args.map((a) => evalNode(a, scope)));
    case "un": {
      const v = evalNode(node.e, scope);
      if (node.op === "!") return !truthy(v);
      const n = num(v);
      return n === null ? null : -n;
    }
    case "tern": return truthy(evalNode(node.c, scope)) ? evalNode(node.a, scope) : evalNode(node.b, scope);
    case "bin": {
      const op = node.op;
      if (op === "&&") { const l = evalNode(node.l, scope); return truthy(l) ? evalNode(node.r, scope) : l; }
      if (op === "||") { const l = evalNode(node.l, scope); return truthy(l) ? l : evalNode(node.r, scope); }
      const l = evalNode(node.l, scope);
      const r = evalNode(node.r, scope);
      switch (op) {
        case "==": return deepEq(l, r);
        case "!=": return !deepEq(l, r);
        case "+":
          if (typeof l === "string" || typeof r === "string") {
            return `${l ?? ""}${r ?? ""}`;
          }
          { const a = num(l), b = num(r); return a === null || b === null ? null : a + b; }
        case "-": { const a = num(l), b = num(r); return a === null || b === null ? null : a - b; }
        case "*": { const a = num(l), b = num(r); return a === null || b === null ? null : a * b; }
        case "/": { const a = num(l), b = num(r); return a === null || b === null || b === 0 ? null : a / b; }
        case "%": { const a = num(l), b = num(r); return a === null || b === null || b === 0 ? null : a % b; }
        case "<": case "<=": case ">": case ">=": {
          if ((typeof l === "number" && typeof r === "number") || (typeof l === "string" && typeof r === "string")) {
            if (op === "<") return l < r;
            if (op === "<=") return l <= r;
            if (op === ">") return l > r;
            return l >= r;
          }
          return false;
        }
        default: throw new Error(`unknown operator '${op}'`);
      }
    }
  }
}

const cache = new Map<string, Node>();
function compile(src: string): Node {
  let ast = cache.get(src);
  if (!ast) {
    ast = new Parser(lex(src)).parse();
    cache.set(src, ast);
  }
  return ast;
}

/** Evaluate a bare expression source (without ${}) against a scope. */
export function evalExpr(src: string, scope: Scope): unknown {
  return evalNode(compile(src), scope);
}

export { truthy };

const SEG = /\$\{([^}]*)\}/g;

/** Resolve a template/expression string. A whole-string ${..} preserves the value's type. */
export function interpolate(str: string, scope: Scope): unknown {
  if (!str.includes("${")) return str;
  const whole = str.match(/^\$\{([^}]*)\}$/);
  if (whole) return evalExpr(whole[1], scope);
  return str.replace(SEG, (_, e) => {
    const v = evalExpr(e, scope);
    return v === null || v === undefined ? "" : String(v);
  });
}

/** Resolve a binding: strings are interpolated; arrays/objects are resolved deeply; else literal. */
export function resolveBinding(value: unknown, scope: Scope): unknown {
  if (typeof value === "string") return interpolate(value, scope);
  if (Array.isArray(value)) return value.map((v) => resolveBinding(v, scope));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = resolveBinding(v, scope);
    return out;
  }
  return value;
}
